import React from 'react'
import { Box, Chip, ListItemButton, Typography } from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import HistoryIcon from '@mui/icons-material/History'
import LaunchIcon from '@mui/icons-material/Launch'
import Inventory2Icon from '@mui/icons-material/Inventory2'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import DescriptionIcon from '@mui/icons-material/Description'
import PersonIcon from '@mui/icons-material/Person'
import SupportAgentIcon from '@mui/icons-material/SupportAgent'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import ForumIcon from '@mui/icons-material/ForumOutlined'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import StorefrontIcon from '@mui/icons-material/Storefront'
import TravelExploreIcon from '@mui/icons-material/TravelExplore'
import ManageSearchIcon from '@mui/icons-material/ManageSearch'
import StickyNote2Icon from '@mui/icons-material/StickyNote2'
import {
  BUTTON_RADIUS, FS, SP, SUCHE_TITEL_ZEILEN, SUCHE_ZEILE_BILD_PX, TOUCH_TARGET_COARSE,
} from '../../theme/tokens'
import { externesZiel } from '../../utils/trefferNavigation'
import { stufeText } from '../../utils/knAnzeige'
import { STATUS_LABEL as INVOICE_STATUS_LABEL } from '../../pages/invoicing/beschriftungen'
import Pill from './Pill'

/**
 * Shared search result row used by the header search bar — the desktop
 * dropdown and the mobile panel. Renders rich content (inventory chip, status chip,
 * variant name, SKU) for products, and subtitle + detail for everything else.
 *
 * @useWhen
 *   - Eine Trefferzeile der globalen Suche rendern (Kopfzeile, Startseite, mobil)
 *   - Ergebnisse ueber MEHRERE Arten hinweg zeigen, die sonst gleich aussaehen
 *
 * @dontUseFor
 *   - Listen EINER Art (dort traegt die Umgebung die Bedeutung) — nimm die
 *     jeweilige Fachkomponente
 *
 * @related
 *   - SidebarAndHeaderBar (Kopfzeilensuche) — 🔴 **der EINZIGE Produktions-Importeur,
 *     mit ZWEI Aufrufstellen**: das Desktop-Dropdown und das Mobil-Panel (M2,
 *     2026-10-16). Eine `@related`-Zeile, die einen Aufrufer behauptet, den es nicht
 *     gibt, schickt die naechste Sitzung an die falsche Stelle — `HomeDashboard` stand
 *     hier bis zum 2026-10-16 und importiert diese Komponente **nicht** (gemessen,
 *     0 Treffer).
 *
 * Props:
 *   result   – search API response item { type, key, title, subtitle, status,
 *              href, detail, extra }
 *   isRecent – show history icon to mark this as a recent search item
 *   onClick  – click handler (caller is responsible for navigation)
 *   sx       – extra MUI sx overrides for the ListItemButton
 *   showKind – Art-Chip zeigen. AUS, wo die Liste nach Art gruppiert ist:
 *              der Gruppenkopf sagt es dort schon (siehe KIND_LABEL)
 *   variante – 🔴 **Suche mobil v2, Stufe 2:** `'mobil'` schaltet die
 *              Shopify-Anatomie ein (40-px-Kachel, Titel mit zwei Zeilen,
 *              EINE Metazeile mit der Status-Pille rechts). Ohne die Prop
 *              rendert die Zeile unveraendert den Desktop-Aufbau; der
 *              Desktop-Aufrufer setzt sie NICHT.
 *   showKindInMeta – nur mit `variante="mobil"`: stellt das Art-Wort der
 *              Metazeile voran. Die Liste setzt es bei „Alle" (Ersatz fuer den
 *              Gruppenkopf) und nicht bei gewaehltem Bereich (Redundanz).
 *   id       – DOM-Id der Zeile. 🔴 Gebraucht vom Mobil-Panel (M2 §5): der
 *              Klebekopf traegt die Art, die Zeile traegt sie NICHT mehr
 *              (`showKind={false}`) — ueber `aria-labelledby` muss sie in den
 *              zugaenglichen Namen zurueck. 🔴 **R16-W-A (2026-10-19):** daraus
 *              leitet die Komponente `<id>-inhalt` ab — ein EIGENES Element um
 *              den Zeileninhalt. DAS ist das Ziel von `aria-labelledby`
 *              (Gruppe + Inhalt). 🔴 **Der Grund ist Klarheit und Robustheit,
 *              NICHT ein Namensverlust:** Accname 1.2 §4.3.2 laesst den
 *              referenzierten Knoten auf Schritt 2F („name from content")
 *              zurueckfallen, auch bei Selbstreferenz — die Zeile selbst zu
 *              referenzieren kostet den Inhalt nicht. Das eigene Element haengt
 *              nicht am Rekursionsschutz und benennt an der Aufrufstelle genau
 *              das, was in den Namen gehoert.
 *
 *   🔴 **Externer Modus (R16-B1, 2026-10-19):** Traegt `result.href` den Marker
 *   `shopify-order:<id>`, rendert die Zeile ein echtes `<a href target="_blank"
 *   rel="noopener noreferrer">` mit `LaunchIcon` (`aria-hidden`) und dem
 *   sr-only-Text „(opens in a new tab)" als Zeileninhalt. Interne Zeilen bleiben
 *   das DEFAULT-Element (`div role="button"`) — ein natives `<button>` darf nur
 *   Phrasing-Content enthalten, die Zeile traegt aber Divs und Chips (R16-W-B).
 *   aria-labelledby – 🔴 Der Preis der Bauform A, bezahlt: „Kopf + eigener
 *              Inhalt" („was ist das" VOR „wie steht es"). Ohne das haette der
 *              sehende Nutzer den Kopf und der blinde die Art verloren.
 */
/**
 * 🔴 Was fuer ein Ding ist dieser Treffer?
 *
 * Bis 2026-08-16 sagte das nichts in der Zeile. Solange die Suche fast nur
 * Faelle und Produkte lieferte, trug der Titel die Bedeutung mit. Mit der Art
 * `rma_communication` (Bahn S2) geht das nicht mehr: eine Nachricht traegt als
 * Titel ihren BETREFF und als Status den des FALLS — sie sieht damit aus wie
 * ein Fall und fuehrt beim Klick auch dorthin.
 *
 * Vom Regel-14-Grill als Blocker benannt: „entweder die Oberflaeche wird
 * angefasst, oder das Ergebnis ist unlesbar."
 *
 * Bewusst KEIN Icon und keine Farbe je Art: die Zeile traegt bereits einen
 * farbigen Status-Chip, und ein zweites farbiges Element daneben laesst beide
 * um Aufmerksamkeit konkurrieren. Ein schmaler, farbloser Chip ordnet ein,
 * ohne zu schreien.
 *
 * 🔴 EXPORTIERT, und das ist der Punkt. Die Trefferliste hatte bereits eine
 * Art-Kennzeichnung — Gruppenkoepfe, die `r.type` an ZWEI Stellen je selbst
 * formatierten. Ein Chip daneben haette ein ZWEITES Vokabular fuer dieselbe
 * Sache eingefuehrt, acht Pixel entfernt: „RMA COMMUNICATION" gegen „Message".
 * Vom Regel-16-Gate als Blocker benannt. Seitdem speisen sich die Koepfe aus
 * dieser Tabelle — ein Wort je Art, an einer Stelle.
 *
 * Nebenbei geheilt: `.replace('_', ' ')` ersetzt nur den ERSTEN Unterstrich,
 * `rma_inbox_email` stand also als „RMA INBOX_EMAIL" im Kopf.
 *
 * ⚠️ `blog_topic` heisst „Blog Topic", NICHT „Blog Source" — auch wenn die
 * Navigation „Blog Sources" sagt. Das sind zwei verschiedene Dinge: die Sicht
 * liest `blog_topic_seeds` (Themenideen), nicht `blog_source_registry`
 * (Publikationsquellen). Eine fruehere Fassung dieser Tabelle richtete sich an
 * der Navigation aus und haette einen Chip „Blog Source" UEBER den Untertitel
 * „Blog Topic" gesetzt — das Untertitel-Literal steht in Migration 226. Vom
 * Regel-16-Gate gefunden. Wer hier ein Wort aendert, prueft die QUELLTABELLE,
 * nicht das Menue.
 *
 * `showKind` gibt es, weil beides zugleich Wiederholung waere: auf den
 * gruppierten Flaechen traegt der Kopf die Art, in der FLACHEN mobilen Liste
 * gibt es keinen Kopf — dort ist der Chip die einzige Kennzeichnung.
 */
export const KIND_LABEL = {
  rma_case:          'Case',
  rma_communication: 'Message',
  rma_inbox_email:   'Inbox mail',
  product:           'Product',
  purchase_order:    'Purchase Order',
  // Stufe N1 (2026-10-19): Shopify-Kunden UND gekoppelte B2B-Kunden. Der
  // Wortlaut ist zeichengleich mit `search_bereiche.BEREICHE[].label`; die
  // Naht haelt `backend/tests/test_search_bereiche.py`.
  customer:          'Customer',
  // Stufe N2 (2026-10-19): Shopify-Bestellungen und Kundenrechnungen. Die
  // Wortlaute sind zeichengleich mit `search_bereiche.BEREICHE[].label`; die
  // Naht haelt `backend/tests/test_search_bereiche.py`.
  order:             'Order',
  invoice:           'Invoice',
  // Stufe N3 (2026-10-20): K&N-Sendungen. Der Wortlaut ist zeichengleich mit
  // `search_bereiche.BEREICHE[].label`; die Naht haelt
  // `backend/tests/test_search_bereiche.py`.
  kn_shipment:       'K&N Shipment',
  vendor:            'Vendor',
  competitor:        'Competitor',
  blog_topic:        'Blog Topic',
  scraper_target:    'Scraper',
}

/**
 * Das Icon der mobilen Kachel — ein Eintrag je Art (Plan §4.2, Stufe 2).
 *
 * 🔴 **Warum hier und nicht in `SidebarAndHeaderBar`:** die Tabelle gehoert zur
 * ZEILE, nicht zur Liste. Stufe 4 ersetzt das Icon durch das Produktbild; die
 * Zuordnung bleibt an derselben Stelle.
 *
 * 🔴 **Jeder Eintrag ist ein Import, der im Repo schon irgendwo steht** — die
 * Namen sind gegen `grep -rho '@mui/icons-material/[A-Za-z0-9]*' frontend/src`
 * geprueft, nicht aus dem Gedaechtnis gewaehlt. Ein Tippfehler im Modulnamen
 * bricht den GESAMTEN Build (CRA zieht alle Importe), nicht nur diese Zeile.
 *
 * Die Icons sind dekorativ: die Kachel traegt `aria-hidden`, die Art steht als
 * Wort in der Metazeile (bei „Alle") bzw. im Chip des Gruppenkopfs.
 */
const KIND_ICON = {
  product:           Inventory2Icon,
  order:             ReceiptLongIcon,
  invoice:           DescriptionIcon,
  customer:          PersonIcon,
  rma_case:          SupportAgentIcon,
  rma_inbox_email:   MailOutlineIcon,
  rma_communication: ForumIcon,
  purchase_order:    ShoppingCartIcon,
  kn_shipment:       LocalShippingIcon,
  vendor:            StorefrontIcon,
  competitor:        TravelExploreIcon,
  scraper_target:    ManageSearchIcon,
  blog_topic:        StickyNote2Icon,
}

/** Ein unbekannter Typ bekommt ein neutrales Icon statt einer leeren Kachel. */
const UNBEKANNT_ICON = ManageSearchIcon

/**
 * Status → Ton der `Pill` (Plan §4.2). Vier Gruppen, mehr nicht:
 * `ok` = aktiv/bezahlt/erledigt, `warn` = offen/ausstehend, `err` = storniert/
 * fehlgeschlagen, **alles Unbekannte `plain`**.
 *
 * 🔴 **Unbekannt ist nie eine Farbe und nie eine Zahl** (Regel 22 §3): ein
 * Status ohne Eintrag rendert `plain` mit seinem Text — nicht `err`, nicht
 * leer. Der Schluessel wird kleingeschrieben verglichen, weil
 * `kn_shipment.current_stage` in VERSALIEN kommt (`IN_TRANSIT`), waehrend
 * `statusLabel` ihn fuer die Anzeige schon uebersetzt.
 */
const STATUS_TONE = {
  // ok — aktiv, bezahlt, erledigt
  active: 'ok', paid: 'ok', fulfilled: 'ok', delivered: 'ok', resolved: 'ok',
  approved: 'ok', received: 'ok', status_updated: 'ok', case_created: 'ok',
  completed: 'ok', closed: 'ok', done: 'ok', mailed: 'ok',
  // warn — offen, ausstehend, unterwegs
  open: 'warn', pending: 'warn', draft: 'warn', with_vendor: 'warn',
  with_customer: 'warn', awaiting_return: 'warn', in_transit: 'warn',
  authorized: 'warn', partially_paid: 'warn', partially_refunded: 'warn',
  notified: 'warn', intent: 'warn', issued: 'warn', sent: 'warn',
  // err — storniert, fehlgeschlagen, zurueckgezahlt
  cancelled: 'err', canceled: 'err', rejected: 'err', refunded: 'err',
  voided: 'err', failed: 'err', error: 'err', undeliverable: 'err',
  archived: 'err', expired: 'err',
}

function statusTone(r) {
  return STATUS_TONE[String(r.status).trim().toLowerCase()] || 'plain'
}

/**
 * Die Beschriftung des Status-Chips — artbewusst, mit EINER Quelle je Art.
 *
 * 🔴 **R16-W1 (2026-10-19).** Die Sicht liefert `invoice.status` roh; die
 * Rechnungsseite fuehrt dagegen eine geschlossene Tabelle
 * (`pages/invoicing/beschriftungen.js::STATUS_LABEL`): `intent` heisst dort
 * **Draft**, `cancelled` **Discarded**. Ein `replace(/_/g, ' ')` zeigte im
 * Suchtreffer „intent" und „cancelled" — zwei Vokabeln fuer denselben Zustand,
 * eine davon falsch. Die Tabelle wird IMPORTIERT, nicht kopiert: eine zweite
 * Abschrift driftet beim naechsten Status.
 */
function statusLabel(r) {
  if (r.type === 'invoice') {
    return INVOICE_STATUS_LABEL[r.status] || r.status.replace(/_/g, ' ')
  }
  // 🔴 R16-B1 (2026-10-20). Die Sicht liefert `current_stage` roh
  // (`IN_TRANSIT`); die Sendungsansicht zeigt denselben Wert ueber `stufeText`
  // (`in transit`). Ein `replace(/_/g, ' ')` zeigte „IN TRANSIT" — zwei
  // Vokabeln fuer denselben Zustand, und die Versalschrift las sich wie eine
  // Ueberzeile. Die Funktion wird IMPORTIERT, nicht nachgebaut: eine zweite
  // Abschrift driftet bei der naechsten Umbenennung.
  if (r.type === 'kn_shipment') {
    return stufeText(r.status)
  }
  return r.status.replace(/_/g, ' ')
}

const RMA_STATUS_COLOR = {
  // Service
  open: 'default', with_vendor: 'info', with_customer: 'warning',
  approved: 'success', rejected: 'error', resolved: 'success',
  // Return workflow
  draft: 'default', awaiting_return: 'warning', in_transit: 'info', received: 'success',
  cancelled: 'default',
  // inbox action_taken
  pending: 'warning', status_updated: 'success', case_created: 'success',
  notified: 'warning', ignored: 'default',
  error: 'error',   // N62: same colour as the inbox chip; fell to `default` before
}

export default function SearchResultItem({
  result: r, isRecent = false, onClick, sx, showKind = true, id, 'aria-labelledby': ariaLabelledby,
  // 🔴 Suche mobil v2, Stufe 2 (Plan §9.4/§10): die MOBILE Anatomie ist eine
  // OPTIONALE Variante. Ohne `variante` rendert die Komponente byte-gleich den
  // bisherigen Aufbau — der Desktop-Aufrufer (`SidebarAndHeaderBar.jsx:1677`)
  // setzt sie nicht, und `V2.10` prueft das am DOM.
  variante,
  // Das Art-Wort steht bei „Alle" in der Metazeile, bei gewaehltem Bereich
  // nicht (Plan §4.2/§10, V2.7). Die Liste entscheidet das — die Zeile kennt
  // den Bereich nicht.
  showKindInMeta = false,
}) {
  const isProduct = r.type === 'product'
  const isRma     = r.type === 'rma_case' || r.type === 'rma_inbox_email'
  const inventory = r.extra?.current_inventory
  const statusColor = isRma ? (RMA_STATUS_COLOR[r.status] || 'default') : undefined
  // 🔴 R16-B1/W3 (2026-10-19): ein externer Marker ist ein ECHTER Link. Die
  // Zeile verlaesst die App, und das muss vor dem Klick sichtbar und im
  // zugaenglichen Namen hoerbar sein. Vorher rief der Klick-Handler
  // `window.open` — bei aktivem Popup-Blocker ein stiller toter Klick.
  const extern = externesZiel(r.href)
  // 🔴 R16-W-A (2026-10-19): Der Zeileninhalt liegt in einem EIGENEN Element
  // mit eigener Id. Der Grund ist Klarheit und Robustheit, NICHT ein
  // Namensverlust: Accname 1.2 §4.3.2 laesst den referenzierten Knoten auf
  // Schritt 2F („name from content") zurueckfallen — auch bei Selbstreferenz
  // bleiben Titel und Unterzeile im Namen. Ein eigenes, explizit referenziertes
  // Inhaltselement haengt nicht am Rekursionsschutz und benennt an der
  // Aufrufstelle genau das, was in den Namen gehoert. Die Id wird aus `id`
  // abgeleitet (`<id>-inhalt`), damit die Aufrufstelle sie nicht zweimal
  // erfinden muss.
  const inhaltId = id ? `${id}-inhalt` : undefined

  // 🔴 R16-W-B (2026-10-19): intern bleibt das DEFAULT-Element
  // (`div role="button"`). Ein natives `<button>` darf nur
  // Phrasing-Content enthalten; die Zeile traegt aber `Box`/`Typography`
  // (Divs) und `Chip`. Extern ist `<a>` richtig — dort ist Flow-Content
  // erlaubt, und der Browser oeffnet den Tab ohne `window.open`.
  // Die Props stehen EINMAL und werden von beiden Varianten geteilt — zwei
  // Abschriften der Link-Logik waeren die Stelle, an der eine davon driftet.
  const zeilenProps = {
    component: extern ? 'a' : undefined,
    href: extern || undefined,
    target: extern ? '_blank' : undefined,
    rel: extern ? 'noopener noreferrer' : undefined,
    onClick,
    id,
    'aria-labelledby': ariaLabelledby,
  }

  // ── Mobile Variante (Suche mobil v2, Stufe 2 Teil A) ─────────────────────
  // Anatomie laut Plan §4.2/§10: 40-px-Kachel + Titel (max. 2 Zeilen) + EINE
  // Metazeile, die Status-Pille rechts IN ihr (F5-A). Die Inhalte der
  // Metazeile kommen aus den Feldern, die die Sicht je Art fuellt
  // (`subtitle`, `detail`, beim Produkt zusaetzlich `extra.current_inventory`)
  // — keine erfundenen Felder (§4.2 „Regel").
  if (variante === 'mobil') {
    const Icon = KIND_ICON[r.type] || UNBEKANNT_ICON
    const metaTeile = []
    // Bei „Alle" ersetzt das Art-Wort den Gruppenkopf (§4.2); bei gewaehltem
    // Bereich waere es Redundanz.
    if (showKindInMeta && KIND_LABEL[r.type]) metaTeile.push(KIND_LABEL[r.type])
    if (r.subtitle) metaTeile.push(r.subtitle)
    if (r.detail) metaTeile.push(r.detail)
    // Regel 22 §3: `null` heisst „die Sicht fuehrt es nicht" — dann steht hier
    // NICHTS. Die 0 dagegen ist eine Aussage und bleibt sichtbar.
    if (isProduct && inventory != null) {
      metaTeile.push(inventory > 0 ? `${inventory} in stock` : 'Out of stock')
    }
    const metaText = metaTeile.join(' · ')

    return (
      <ListItemButton
        {...zeilenProps}
        sx={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: SP.tight,
          // 🔴 Plan §2.2: das Zeilenpolster faellt bewusst von 12 auf 8 je
          // Seite (`py: 1.5` → `py: 1`). Das ist die halbe Bedingung fuer
          // A1 = 9; wer sie zuruecknimmt, rechnet 85,5 px je Zeile.
          py: 1,
          px: 2,
          ...TOUCH_TARGET_COARSE,
          ...sx,
        }}
      >
        <Box
          id={inhaltId}
          sx={{
            display: 'flex', flexDirection: 'row', alignItems: 'center',
            gap: SP.tight, width: '100%', minWidth: 0,
          }}
        >
          {/* Die Kachel: Icon in Stufen 2–3, Produktbild ab Stufe 4 (§4.3).
              Dekorativ — die Art steht als Wort in der Metazeile. */}
          <Box
            aria-hidden="true"
            data-testid="search-result-bild"
            sx={{
              width: SUCHE_ZEILE_BILD_PX,
              height: SUCHE_ZEILE_BILD_PX,
              flexShrink: 0,
              borderRadius: `${BUTTON_RADIUS}px`,
              bgcolor: 'action.hover',
              color: 'text.secondary',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <Icon sx={{ fontSize: FS.xl }} />
          </Box>
          <Box
            sx={{
              display: 'flex', flexDirection: 'column', alignItems: 'stretch',
              flexGrow: 1, minWidth: 0,
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontWeight: 600,
                // 🔴 F2-A: zwei Zeilen, dann kuerzen. Ohne `display` tut
                // `-webkit-line-clamp` nichts; ohne `overflow` laeuft der Rest
                // heraus.
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: SUCHE_TITEL_ZEILEN,
                overflow: 'hidden',
                width: '100%',
              }}
            >
              {r.title}
            </Typography>
            {(metaText || !!r.status || extern || isRecent) && (
              <Box
                data-testid="search-result-meta"
                sx={{
                  display: 'flex', alignItems: 'center', gap: SP.chip,
                  width: '100%', minWidth: 0,
                }}
              >
                {isRecent && (
                  <HistoryIcon sx={{ color: 'text.secondary', fontSize: FS.xl, flexShrink: 0 }} />
                )}
                {metaText && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    noWrap
                    sx={{ flexGrow: 1, minWidth: 0, fontSize: FS.sm, lineHeight: 1.4 }}
                  >
                    {metaText}
                  </Typography>
                )}
                {extern && (
                  <>
                    <LaunchIcon
                      aria-hidden="true"
                      data-testid="search-result-external-icon"
                      sx={{ color: 'text.secondary', fontSize: FS.sm, flexShrink: 0 }}
                    />
                    <Box component="span" sx={visuallyHidden}>(opens in a new tab)</Box>
                  </>
                )}
                {/* 🔴 F5-A: die Pille steht IN der Metazeile, rechts. `py: 0`
                    macht sie 19,5 px hoch (17,5 Zeile + 0 + 2 Rand) statt 27,5 —
                    ohne das wird die Zeile 85,5 px hoch und A1 faellt auf 8.
                    `0` ist hier kein Abstandswert, sondern „kein Polster"; ein
                    SP-Token dafuer gibt es nicht (Plan §4.2/W28).
                    🔴 `nowrap` (Nachbesserung 1, 01.11.2026): `Pill` setzt
                    `whiteSpace: 'nowrap'` NUR mit diesem Prop. Ohne ihn bricht
                    ein zweiwortiger Status am Leerzeichen um, die Metazeile
                    waechst von 19,5 auf 37 px und die Zeile auf 95 — gemessen
                    an `kn_shipment` („in transit"). Der laengste Status der
                    Sicht ist „partially refunded" (18 Zeichen, `order`). */}
                {!!r.status && (
                  <Pill
                    data-testid="search-result-status"
                    tone={statusTone(r)}
                    nowrap
                    sx={{ py: 0 }}
                  >
                    {statusLabel(r)}
                  </Pill>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </ListItemButton>
    )
  }

  return (
    <ListItemButton
      {...zeilenProps}
      sx={{
        flexDirection: 'column',
        alignItems: 'flex-start',
        py: 1.5,
        px: 2,
        // 🔴 M2 (2026-10-16) — der Zeilenboden bei grobem Zeiger. Heute traegt
        // `ListItemButton` (ein `ButtonBase`) den Theme-Boden NICHT
        // (`pattern-library.md:1357-1361`); eine reine Titelzeile misst
        // 20 + 24 = genau 44 px. Die Deklaration ist ein GURT, keine Reparatur:
        // sie steht da, damit die Zusage im Kode steht und nicht im Zufall der
        // Zeilenanzahl (eine Zeile ohne Status-Chip waere schmaler).
        ...TOUCH_TARGET_COARSE,
        ...sx,
      }}
    >
      {/* R16-W-A: der Inhalt als GANZES, mit eigener Id — das ist das Ziel von
          `aria-labelledby` an der Mobil-Aufrufstelle (Gruppe + Inhalt). */}
      <Box
        id={inhaltId}
        sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}
      >
        {/* Title row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
          {isRecent && <HistoryIcon sx={{ color: 'text.secondary', fontSize: 18 }} />}
          {/* 🔴 M2 §2(b) — `minWidth: 0` ist eine EIGENE Zeile, keine Zugabe:
              `noWrap` setzt `white-space: nowrap`, und in einer Flex-Zeile ist
              `min-width: auto` dann die MAX-CONTENT-Breite — `flexShrink: 1` kann
              darunter nicht druecken. Ein langer Titel (RMA-AAC-1783323718442868)
              schoebe die Zeile ueber den Behaelter, auch ohne den Art-Chip.
              Gemessen in §2: welche der drei Stellen die 792 px erzeugt, war
              nicht belegt — beide Ursachen werden deshalb abgestellt.
              §5(a) — die Skala: `body1` (14) + 600 ist die Haus-Fliesstextgroesse
              (`FS.base`), `subtitle2` war 12,25. */}
          <Typography variant="body1" noWrap sx={{ flexGrow: 1, minWidth: 0, fontWeight: 600 }}>
            {r.title}
          </Typography>
          {/* Art zuerst, Status danach: die Art beantwortet „was ist das",
              der Status „wie steht es". Eine unbekannte Art rendert NICHTS,
              statt einen leeren Chip zu zeigen. */}
          {showKind && !!KIND_LABEL[r.type] && (
            <Chip
              size="small"
              variant="outlined"
              label={KIND_LABEL[r.type]}
              data-testid="search-result-kind"
              // FS.xs ist die Marke fuer dichte Chip-Etiketten. Die rohe 10 hier
              // war exakt der Wert, den FS.xs bei der Skalenanhebung am
              // 2026-08-09 VERLOREN hat (tokens.js: `xs: 11.5, // was 10`) —
              // also unterhalb der gesamten lebenden Skala.
              // flexShrink: 0, weil MUI auf der Chip-Wurzel `overflow: hidden`
              // setzt: ohne das kuerzt der Chip sein eigenes Etikett, sobald die
              // Zeile eng wird. Das trifft die mobile Liste, also genau die
              // Flaeche, auf der er die einzige Kennzeichnung ist.
              sx={{ height: 20, fontSize: FS.xs, color: 'text.secondary', flexShrink: 0 }}
            />
          )}
          {!!r.status && (
            // 🔴 M2 §5(a): der Status-Chip stand auf MUIs 11,375 px — die Zeile
            // mischte damit DREI Skalen (11,5 / 11,375 / 10,5). Er kommt auf
            // `FS.xs` wie das Art-Etikett daneben: eine Leiter, zwei Etiketten.
            <Chip size="small" label={statusLabel(r)}
              color={statusColor || 'default'}
              sx={{ fontSize: FS.xs, flexShrink: 0 }} />
          )}
          {/* 🔴 R16-B1: sichtbares Merkmal fuer „verlaesst die App" (Hausform
              `ShopifyLink` in `CustomerDetail.jsx`: LaunchIcon + Hinweis im
              zugaenglichen Namen). Das Icon ist dekorativ (`aria-hidden`), der
              sr-only-Text traegt die Ansage — auch im mobilen
              `aria-labelledby`, weil er INHALT der Zeile ist. */}
          {extern && (
            <>
              <LaunchIcon
                aria-hidden="true"
                data-testid="search-result-external-icon"
                sx={{ color: 'text.secondary', fontSize: FS.sm, flexShrink: 0 }}
              />
              <Box component="span" sx={visuallyHidden}>(opens in a new tab)</Box>
            </>
          )}
        </Box>

        {/* Variant name (products only) */}
        {isProduct && r.detail && (
          <Typography
            variant="body2"
            color="text.secondary"
            noWrap
            // 🔴 M2 §5(a): die Unterzeile steigt auf `FS.sm` (12,5) — dieselbe
            // Stufe wie die Sekundaertexte der Tabellen (`tokens.js:284`).
            sx={{ width: '100%', fontSize: FS.sm, pl: isRecent ? 3.5 : 0 }}
          >
            {r.detail}
          </Typography>
        )}

        {/* SKU + inventory chip (products only) */}
        {isProduct && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', pl: isRecent ? 3.5 : 0 }}>
            {/* 🔴 M2 §2(c) — dieselbe Klasse wie Titel und Zusammenfassung:
                `noWrap` ohne `minWidth: 0` in einer Zeilen-Flexbox. Hier ist die
                Abhilfe eine ANDERE als bei (a): `width: '100%'` waere falsch, es
                schoebe den Bestands-Chip aus der Zeile. Richtig ist `minWidth: 0`
                — der Text darf schrumpfen, der Chip bleibt. */}
            <Typography variant="caption" color="text.secondary" noWrap sx={{ minWidth: 0, fontSize: FS.sm }}>
              {r.subtitle}
            </Typography>
            {inventory != null && (
              <Chip
                size="small"
                label={inventory > 0 ? `${inventory} in stock` : 'Out of stock'}
                color={inventory > 0 ? 'success' : 'default'}
                // 🔴 M2 §5(a): die rohe 10 war genau der Wert, den `FS.xs` bei der
                // Skalenanhebung am 2026-08-09 VERLOREN hat (`tokens.js:282`:
                // `xxs: 10.5, // was 9`, `xs: 11.5, // was 10`) — die Zeile stand
                // unterhalb der ganzen lebenden Skala. Der Bestands-Chip ist eine
                // Mikro-Marke, also `FS.xxs`, nicht `FS.xs`: er traegt eine Zahl,
                // kein Etikett.
                sx={{ height: 20, fontSize: FS.xxs, flexShrink: 0 }}
              />
            )}
          </Box>
        )}

        {/* Subtitle + detail (non-products only) */}
        {!isProduct && (
          <>
            {!!r.subtitle && (
              <Typography
                variant="body2"
                color="text.secondary"
                noWrap
                sx={{ width: '100%', fontSize: FS.sm, pl: isRecent ? 3.5 : 0 }}
              >
                {r.subtitle}
              </Typography>
            )}
            {!!r.detail && (
              <Typography
                variant="caption"
                color="text.secondary"
                noWrap
                // 🔴 M2 §2(a) — die Zeile, die in `03b_Test_treffer.png` OHNE
                // Auslassung rechts aus dem Bild laeuft, waehrend die Schwesterzeile
                // darueber kuerzt. Der Unterschied ist genau `width: '100%'`: erst
                // damit ist die Breite DEFINIT (398 px) und `overflow: hidden` +
                // `text-overflow: ellipsis` greifen. Bei `width: auto` in einer
                // Spalten-Flexbox ist die Querachse `fit-content`, und bei `nowrap`
                // ist `min-content == max-content` — die verfuegbaren 398 px klemmen
                // nicht, das Element waechst ueber seinen Behaelter hinaus.
                // §5(a): dieselbe Stufe wie die Unterzeile, `FS.sm`.
                sx={{ width: '100%', fontSize: FS.sm, pl: isRecent ? 3.5 : 0 }}
              >
                {r.detail}
              </Typography>
            )}
          </>
        )}
      </Box>
    </ListItemButton>
  )
}
